"use client"

import {UiErrorText} from "@/components/i18n/ui-error-text"
import { FormEvent, useEffect, useMemo, useRef, useState } from "react"
import { ExternalLink, Loader2, PhoneOutgoing } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { useStartOutgoingVoiceCallMutation } from "@/hooks/mutations/outgoing-voice-calls"
import { useOutgoingVoiceCallPhoneNumbersQuery } from "@/hooks/queries/outgoing-voice-calls"
import type { VoiceAgent } from "@/hooks/agents/voice"
import { OutgoingCallNumberSelect } from "@/components/agents/voice/outgoing-call-number-select"
import { useTranslations } from "next-intl"



type OutgoingCallDialogProps = {
  agent: VoiceAgent | null
  agents?: VoiceAgent[]
  isLoadingAgents?: boolean
  agentsError?: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

type OutgoingCallField = "voiceAgentId" | "fromNumber" | "toNumber" | "customInstructions"
type OutgoingCallFieldErrors = Partial<Record<OutgoingCallField, string>>

const LAST_OUTGOING_CALL_FROM_NUMBER_KEY = "versafy:last-outgoing-call-from-number"
const EMPTY_PHONE_NUMBERS: NonNullable<ReturnType<typeof useOutgoingVoiceCallPhoneNumbersQuery>["data"]> = []
const EMPTY_AGENTS: VoiceAgent[] = []

export function OutgoingCallDialog({
  agent,
  agents = EMPTY_AGENTS,
  isLoadingAgents = false,
  agentsError = null,
  open,
  onOpenChange,
}: OutgoingCallDialogProps) {

  const tUi = useTranslations("Voice.AgentsVoiceOutgoingCallDialog")
  const tCommonUi = useTranslations("Common")

  const shouldSelectAgent = !agent
  const [selectedAgentId, setSelectedAgentId] = useState("")
  const [fromNumber, setFromNumber] = useState("")
  const [toNumber, setToNumber] = useState("")
  const [customInstructions, setCustomInstructions] = useState("")
  const [personalizeOpeningMessage, setPersonalizeOpeningMessage] = useState(true)
  const [validationErrors, setValidationErrors] = useState<OutgoingCallFieldErrors>({})
  const { data: phoneNumbers = EMPTY_PHONE_NUMBERS, isLoading, error } = useOutgoingVoiceCallPhoneNumbersQuery(open)
  const startCallMutation = useStartOutgoingVoiceCallMutation()
  const resetStartCallMutationRef = useRef(startCallMutation.reset)

  const availableNumbers = useMemo(
    () => phoneNumbers.filter(number => number.phoneNumber && !number.lendingSourceTenantId),
    [phoneNumbers],
  )
  const selectableAgents = useMemo(
    () => agents.filter(candidate => candidate.status === "active"),
    [agents],
  )
  const selectedAgent = useMemo(
    () => agent ?? selectableAgents.find(candidate => candidate.id === selectedAgentId) ?? null,
    [agent, selectableAgents, selectedAgentId],
  )

  useEffect(() => {
    if (!open) {
      setSelectedAgentId("")
      setFromNumber("")
      setToNumber("")
      setCustomInstructions("")
      setPersonalizeOpeningMessage(true)
      setValidationErrors({})
      resetStartCallMutationRef.current()
    }
  }, [open])

  useEffect(() => {
    resetStartCallMutationRef.current = startCallMutation.reset
  }, [startCallMutation.reset])

  useEffect(() => {
    if (!open || fromNumber || availableNumbers.length === 0) return

    const lastUsedFromNumber = window.localStorage.getItem(LAST_OUTGOING_CALL_FROM_NUMBER_KEY)
    const savedNumberIsAvailable = availableNumbers.some(number => number.phoneNumber === lastUsedFromNumber)
    setFromNumber(savedNumberIsAvailable && lastUsedFromNumber ? lastUsedFromNumber : availableNumbers[0].phoneNumber)
  }, [availableNumbers, fromNumber, open])

  useEffect(() => {
    if (!open || !fromNumber || availableNumbers.length === 0) return

    const selectedNumberIsAvailable = availableNumbers.some(number => number.phoneNumber === fromNumber)
    if (!selectedNumberIsAvailable) {
      setFromNumber(availableNumbers[0].phoneNumber)
    }
  }, [availableNumbers, fromNumber, open])

  useEffect(() => {
    if (!open || !shouldSelectAgent || selectedAgentId || selectableAgents.length === 0) return

    setSelectedAgentId(selectableAgents[0].id)
  }, [open, selectableAgents, selectedAgentId, shouldSelectAgent])

  useEffect(() => {
    if (!open || !shouldSelectAgent || !selectedAgentId || selectableAgents.length === 0) return

    const selectedAgentIsAvailable = selectableAgents.some(candidate => candidate.id === selectedAgentId)
    if (!selectedAgentIsAvailable) {
      setSelectedAgentId(selectableAgents[0].id)
    }
  }, [open, selectableAgents, selectedAgentId, shouldSelectAgent])

  const isSubmitting = startCallMutation.isPending
  const canSubmit = Boolean(selectedAgent?.id && fromNumber && toNumber && !isSubmitting)

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!selectedAgent || !canSubmit) return

    try {
      const trimmedCustomInstructions = customInstructions.trim()
      await startCallMutation.mutateAsync({
        voiceAgentId: selectedAgent.id,
        fromNumber,
        toNumber,
        customInstructions: trimmedCustomInstructions || undefined,
        personalizeOpeningMessage,
        setErrors: errors => setValidationErrors(getOutgoingCallFieldErrors(errors)),
      })
      window.localStorage.setItem(LAST_OUTGOING_CALL_FROM_NUMBER_KEY, fromNumber)

      onOpenChange(false)
    } catch {
      // withQueryHandling already renders the error toast. Keep the dialog
      // open and avoid surfacing handled mutation failures to the dev overlay.
    }
  }

  function clearValidationError(field: OutgoingCallField) {
    setValidationErrors(current => {
      if (!current[field]) return current

      const next = { ...current }
      delete next[field]
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle><span key={tUi("makeACall")}>{tUi("makeACall")}</span></DialogTitle>
          <DialogDescription>
            <span><span key={tUi("startAnOutgoingCallFrom")}>{tUi("startAnOutgoingCallFrom")}</span></span> {selectedAgent?.name ?? tUi("aSelectedVoiceAgent")}<span><span key={tUi("onlyPhoneNumbersWithOutboundCallingEnabledOn")}>{tUi("onlyPhoneNumbersWithOutboundCallingEnabledOn")}</span></span>
            {" "}
            <br/>
            <br/>
            <span><span key={tUi("youCanAlsoStartCallsThroughThe")}>{tUi("youCanAlsoStartCallsThroughThe")}</span></span>{" "}
            <a
              href="/api/tenant-api/docs"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              <span><span key={tUi("tenantAPI")}>{tUi("tenantAPI")}</span></span>
              <ExternalLink className="h-3 w-3" />
            </a>
            <span>.</span>
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {agentsError && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                {agentsError}
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">
                <span key={error instanceof Error ? error.message : tUi("failedToLoadPhoneNumbers")}><UiErrorText message={error instanceof Error ? error.message : undefined} fallback={tUi("failedToLoadPhoneNumbers")} /></span>
              </AlertDescription>
            </Alert>
          )}

          {shouldSelectAgent && (
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="outgoing-voice-agent">
                <span key={tUi("voiceAgent")}>{tUi("voiceAgent")}</span></label>
              <Select
                value={selectedAgentId}
                onValueChange={(value) => {
                  setSelectedAgentId(value)
                  clearValidationError("voiceAgentId")
                }}
                disabled={isLoadingAgents || isSubmitting}
              >
                <SelectTrigger
                  id="outgoing-voice-agent"
                  className={validationErrors.voiceAgentId ? "border-destructive focus:ring-destructive" : undefined}
                  aria-invalid={Boolean(validationErrors.voiceAgentId)}
                  aria-describedby={validationErrors.voiceAgentId ? "outgoing-voice-agent-error" : undefined}
                >
                  <SelectValue placeholder={isLoadingAgents ? tUi("loadingVoiceAgents") : tUi("selectAVoiceAgent")} />
                </SelectTrigger>
                <SelectContent>
                  {selectableAgents.map(candidate => (
                    <SelectItem key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {validationErrors.voiceAgentId ? (
                <p id="outgoing-voice-agent-error" role="alert" className="text-xs font-medium text-destructive">
                  {validationErrors.voiceAgentId}
                </p>
              ) : null}
              {!isLoadingAgents && selectableAgents.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  <span key={tUi("noActiveVoiceAgentsAreAvailableActivateA")}>{tUi("noActiveVoiceAgentsAreAvailableActivateA")}</span></p>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="outgoing-from-number">
              <span key={tUi("callFrom")}>{tUi("callFrom")}</span></label>
            <OutgoingCallNumberSelect
              numbers={phoneNumbers}
              value={fromNumber}
              loading={isLoading}
              disabled={isLoading || isSubmitting}
              invalid={Boolean(validationErrors.fromNumber)}
              describedBy={validationErrors.fromNumber ? "outgoing-from-number-error" : undefined}
              onValueChange={(value) => {
                setFromNumber(value)
                clearValidationError("fromNumber")
              }}
            />
            {validationErrors.fromNumber ? (
              <p id="outgoing-from-number-error" role="alert" className="text-xs font-medium text-destructive">
                {validationErrors.fromNumber}
              </p>
            ) : null}
            {!isLoading && phoneNumbers.length === 0 && (
              <p className="text-xs text-muted-foreground">
                <span key={tUi("noPhoneNumbersAreEnabledForOutboundCalling")}>{tUi("noPhoneNumbersAreEnabledForOutboundCalling")}</span></p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="outgoing-to-number">
              <span key={tUi("recipientNumber")}>{tUi("recipientNumber")}</span></label>
            <Input
              id="outgoing-to-number"
              value={toNumber}
              onChange={(event) => {
                setToNumber(event.target.value)
                clearValidationError("toNumber")
              }}
              placeholder="+15551234567"
              autoComplete="tel"
              autoCorrect="off"
              spellCheck={false}
              disabled={isSubmitting}
              className={validationErrors.toNumber ? "border-destructive focus-visible:ring-destructive" : undefined}
              aria-invalid={Boolean(validationErrors.toNumber)}
              aria-describedby={validationErrors.toNumber
                ? "outgoing-to-number-error"
                : "outgoing-to-number-description"}
            />
            <p id="outgoing-to-number-description" className="text-xs text-muted-foreground">
              <span key={tUi("useE164FormatIncludingTheCountryCode")}>{tUi("useE164FormatIncludingTheCountryCode")}</span></p>
            {validationErrors.toNumber ? (
              <p id="outgoing-to-number-error" role="alert" className="text-xs font-medium text-destructive">
                {validationErrors.toNumber}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="outgoing-custom-instructions">
              <span key={tUi("customInstructions")}>{tUi("customInstructions")}</span></label>
            <Textarea
              id="outgoing-custom-instructions"
              value={customInstructions}
              onChange={(event) => {
                setCustomInstructions(event.target.value)
                clearValidationError("customInstructions")
              }}
              placeholder={tUi("optionalContextOrInstructionsForThisSpecificCall")}
              maxLength={2000}
              disabled={isSubmitting}
              className={validationErrors.customInstructions
                ? "border-destructive focus-visible:ring-destructive"
                : undefined}
              aria-invalid={Boolean(validationErrors.customInstructions)}
              aria-describedby={validationErrors.customInstructions
                ? "outgoing-custom-instructions-error"
                : "outgoing-custom-instructions-description"}
            />
            <p id="outgoing-custom-instructions-description" className="text-xs text-muted-foreground">
              <span key={tUi("optionalExtraContextForThisCallOnlyDoes")}>{tUi("optionalExtraContextForThisCallOnlyDoes")}</span></p>
            <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1 pt-1">
              <Checkbox
                id="outgoing-personalize-opening-message"
                checked={personalizeOpeningMessage}
                onCheckedChange={checked => setPersonalizeOpeningMessage(checked === true)}
                disabled={isSubmitting}
                aria-describedby="outgoing-opening-message-description"
              />
              <label className="min-w-0 cursor-pointer text-sm font-medium" htmlFor="outgoing-personalize-opening-message">
                <span key={tUi("personalizeOpeningMessage")}>{tUi("personalizeOpeningMessage")}</span></label>
              <p id="outgoing-opening-message-description" className="col-start-2 min-w-0 text-xs text-muted-foreground">
                <span key={tUi("useTheseInstructionsToTailorTheGreetingFor")}>{tUi("useTheseInstructionsToTailorTheGreetingFor")}</span></p>
            </div>
            {validationErrors.customInstructions ? (
              <p
                id="outgoing-custom-instructions-error"
                role="alert"
                className="text-xs font-medium text-destructive"
              >
                {validationErrors.customInstructions}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span key={tUi("starting")}>{tUi("starting")}</span></>
              ) : (
                <>
                  <PhoneOutgoing className="w-4 h-4 mr-2" />
                  <span key={tUi("startCall")}>{tUi("startCall")}</span></>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function getOutgoingCallFieldErrors(errors: Record<string, unknown>): OutgoingCallFieldErrors {
  return {
    voiceAgentId: getStringError(errors.voiceAgentId),
    fromNumber: getStringError(errors.fromNumber),
    toNumber: getStringError(errors.toNumber),
    customInstructions: getStringError(errors.customInstructions),
  }
}

function getStringError(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined
}
